import { getStringTag } from 'features/libra/helpers/getStringTag'
import { useComponentOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useRolodexEntriesWithSyncedRefreshes } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import { GeckoAssetModel, GeckoComponent, RolodexEntryType } from 'types'

import { resolveTicketAssetModel } from './ffsAssetModelSnapshot'
import { getExpectedFfsAssetModelSnapshotId } from './ffsIds'

export interface FfsAssetModelSnapshotResult {
  /**
   * The asset model the ticket should read: the ticket's snapshot when one
   * exists, else the unit's live model (legacy tickets, read-only viewers
   * before first snapshot, non-asset-scoped workflows).
   */
  assetModel: GeckoAssetModel | undefined
  /** True when `assetModel` is the ticket-scoped snapshot. */
  isSnapshot: boolean
  /** True while the workflow is asset-scoped (component ontology declared). */
  isAssetScoped: boolean
  /**
   * The unit id the asset-model queries are keyed on. Canonical for the
   * whole ticket: ticket `unit` tag, else (legacy tickets bound by
   * COMPONENT tag only) the component entry's `unit` tag. Exposed so
   * `FfsAssetModelSnapshotProvider` builds the snapshot from the same live
   * model every consumer reads.
   */
  unitId: string | undefined
  /** The deterministic snapshot entry id for this ticket. */
  snapshotId: string
  /** True until the snapshot + live queries have both resolved. */
  isLoading: boolean
  /** Refreshes both the snapshot and live queries. */
  forceRefresh: () => Promise<void>
}

/**
 * Ticket-scoped asset-model READ resolution for FFS workflows. Pure read --
 * snapshot creation is owned by a single `FfsAssetModelSnapshotProvider`
 * mount (see `FfsAssetModelSnapshotContext.tsx`), so the ~6 concurrent
 * mounts of this hook on a ticket page can never race a create or disagree
 * about failure state.
 *
 * Asset-scoped workflows (non-empty `useComponentOntologyFields()`) read a
 * per-ticket SNAPSHOT of the unit's asset model
 * (`ffs-asset-model_{ticketId}`) so in-workflow analysis and asset-model
 * edits are isolated from the unit's live model. The snapshot entry carries
 * no `unit` tag, keeping it invisible to every unit-scoped ASSET_MODEL
 * query. Non-asset-scoped (legacy single-component) workflows skip the
 * snapshot query and resolve the live model exactly as before. Write-back
 * to the live model is a future task.
 *
 * Both queries use stable shapes, so SWR dedupes across every mount. Must
 * be called inside `TicketAccessProvider`, `WorkflowProvider`, and
 * `RolodexEntriesWithSyncedRefreshesProvider`.
 */
export function useFfsAssetModelSnapshot(): FfsAssetModelSnapshotResult {
  const { ticketId, ticketTags } = useTicketAccess()
  const { ontologyFields: componentOntologyFields } =
    useComponentOntologyFields()
  // Non-empty component ontology is the established "this workflow is
  // asset-scoped" marker on this branch.
  const isAssetScoped = Object.keys(componentOntologyFields).length > 0

  // Canonical unitId: prefer the ticket's own unit tag; fall back to the
  // ticket's COMPONENT tag entry (legacy tickets are sometimes bound by
  // component only). The lookup query mounts only when the fallback is
  // actually needed.
  const ticketUnitId = getStringTag(ticketTags, RolodexEntryType.UNIT)
  const ticketComponentId = getStringTag(ticketTags, RolodexEntryType.COMPONENT)
  const { entries: fallbackComponentEntries } =
    useRolodexEntriesWithSyncedRefreshes(
      !ticketUnitId && ticketComponentId
        ? { type: RolodexEntryType.COMPONENT, ids: [ticketComponentId] }
        : undefined
    )
  const fallbackComponent = fallbackComponentEntries[0] as
    | GeckoComponent
    | undefined
  const unitId =
    ticketUnitId ??
    (fallbackComponent
      ? getStringTag(fallbackComponent.tags, RolodexEntryType.UNIT)
      : undefined)

  const snapshotId = getExpectedFfsAssetModelSnapshotId({ ticketId })

  const {
    entries: snapshotEntries,
    isLoading: snapshotLoading,
    forceRefresh: refreshSnapshot,
  } = useRolodexEntriesWithSyncedRefreshes(
    isAssetScoped
      ? { type: RolodexEntryType.ASSET_MODEL, ids: [snapshotId] }
      : undefined
  )

  // Same query shape as the pre-snapshot fetch paths. Snapshots never
  // appear here (no unit tag).
  const {
    entries: liveEntries,
    isLoading: liveLoading,
    forceRefresh: refreshLive,
  } = useRolodexEntriesWithSyncedRefreshes(
    unitId
      ? {
          type: RolodexEntryType.ASSET_MODEL,
          tags: { [RolodexEntryType.UNIT]: unitId },
        }
      : undefined
  )

  const resolved = useMemo(
    () => resolveTicketAssetModel({ snapshotEntries, liveEntries }),
    [snapshotEntries, liveEntries]
  )

  const forceRefresh = useCallback(async (): Promise<void> => {
    await Promise.all([refreshSnapshot(), refreshLive()])
  }, [refreshSnapshot, refreshLive])

  return useMemo(
    () => ({
      assetModel: resolved.assetModel,
      isSnapshot: resolved.isSnapshot,
      isAssetScoped,
      unitId,
      snapshotId,
      isLoading: snapshotLoading || liveLoading,
      forceRefresh,
    }),
    [
      resolved,
      isAssetScoped,
      unitId,
      snapshotId,
      snapshotLoading,
      liveLoading,
      forceRefresh,
    ]
  )
}
